import React from 'react';
import { lazy } from 'react';
import { RouteObject, Navigate } from 'react-router-dom';
import PublicLayout from '@/components/layout/PublicLayout';
import DashboardLayout from '@/components/layout/DashboardLayout';
import AdminLayout from '@/components/layout/AdminLayout';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import NoIndex from '@/components/seo/NoIndex';

// Lazy load pages
const HomePage = lazy(() => import('@/pages/home/page'));
const VisionPage = lazy(() => import('@/pages/vision/page'));
const ApporteursAffairesPage = lazy(() => import('@/pages/apporteursAffaires/page'));
const CollaborateursPage = lazy(() => import('@/pages/collaborateurs/page'));
const CommentCaMarchePage = lazy(() => import('@/pages/commentCaMarche/Page'));
const FAQPage = lazy(() => import('@/pages/faq/Page'));
const BlogListPage = lazy(() => import('@/pages/blog/BlogListPage'));
const BlogPostPage = lazy(() => import('@/pages/blog/BlogPostPage'));
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const SignupPage = lazy(() => import('@/pages/auth/SignupPage'));
const ResetPage = lazy(() => import('@/pages/auth/ResetPage'));
const InscriptionConfirmeePage = lazy(() => import('@/pages/auth/InscriptionConfirmeePage'));
const MentionsLegalesPage = lazy(() => import('@/pages/legal/MentionsLegalesPage'));
const CGUPage = lazy(() => import('@/pages/legal/CGUPage'));
const PolitiqueConfidentialitePage = lazy(() => import('@/pages/legal/PolitiqueConfidentialitePage'));
const CookiesPage = lazy(() => import('@/pages/legal/CookiesPage'));
const MerciAOPage = lazy(() => import('@/pages/merci/MerciAOPage'));
const MerciCommercePage = lazy(() => import('@/pages/merci/MerciCommercePage'));
const NotFoundPage = lazy(() => import('@/pages/NotFound'));

// City pages
const CollaborateursCityPage = lazy(() => import('@/pages/collaborateurs/ville/CityPage'));
const ApporteursAffairesCityPage = lazy(() => import('@/pages/apporteursAffaires/ville/CityPage'));

// City + Sector pages
const CollabCitySectorPage = lazy(() => import('@/pages/collaborateurs/ville/CitySectorPage'));
const AOCitySectorPage = lazy(() => import('@/pages/apporteursAffaires/ville/CitySectorPage'));

// Villes & Départements pages
const VillesPage = lazy(() => import('@/pages/villes/Page'));
const DepartementsIndexPage = lazy(() => import('@/pages/departements/Page'));
const DepartementCityListPage = lazy(() => import('@/pages/departements/CityListPage'));

// Régions pages
const RegionsIndexPage = lazy(() => import('@/pages/regions/Page'));
const RegionCityListPage = lazy(() => import('@/pages/regions/CityListPage'));

// Secteurs pages
const CollabSectorsIndexPage = lazy(() => import('@/pages/collaborateurs/secteurs/Page'));
const CollabSectorPage = lazy(() => import('@/pages/collaborateurs/secteur/SectorPage'));
const AOSectorsIndexPage = lazy(() => import('@/pages/apporteursAffaires/secteurs/Page'));
const AOSectorPage = lazy(() => import('@/pages/apporteursAffaires/secteur/SectorPage'));

// Dashboard pages
const DashboardRouterPage = lazy(() => import('../pages/dashboard/DashboardRouterPage'));
const ComptePage = lazy(() => import('../pages/dashboard/ComptePage'));
const CandidaturePage = lazy(() => import('../pages/dashboard/CandidaturePage'));
const SupportPage = lazy(() => import('../pages/dashboard/SupportPage'));
const NotificationsPage = lazy(() => import('../pages/dashboard/NotificationsPage'));

// AO Dashboard pages
const AOOverviewPage = lazy(() => import('../pages/dashboard/ao/OverviewPage'));
const AOOpportunitesPage = lazy(() => import('../pages/dashboard/ao/OpportunitesPage'));
const AOCommissionsPage = lazy(() => import('../pages/dashboard/ao/CommissionsPage'));
const AOClassementPage = lazy(() => import('../pages/dashboard/ao/ClassementPage'));
const AOAffiliesPage = lazy(() => import('../pages/dashboard/ao/AffiliesPage'));
const AOParrainagePage = lazy(() => import('../pages/dashboard/ao/ParrainagePage'));

// Collaborateur Dashboard pages
const CollabOverviewPage = lazy(() => import('@/pages/dashboard/collaborateur/OverviewPage'));
const CollabDocumentsPage = lazy(() => import('@/pages/dashboard/collaborateur/DocumentsPage'));
const CollabMarquesPage = lazy(() => import('@/pages/dashboard/collaborateur/MarquesPage'));
const CollabRendezVousPage = lazy(() => import('@/pages/dashboard/collaborateur/RendezVousPage'));

// Admin pages
const AdminPage = lazy(() => import('@/pages/admin/AdminPage'));
const CandidaturesPage = lazy(() => import('@/pages/admin/CandidaturesPage'));
const StatistiquesPage = lazy(() => import('@/pages/admin/StatistiquesPage'));
const UtilisateursPage = lazy(() => import('@/pages/admin/UtilisateursPage'));
const ApporteursPage = lazy(() => import('@/pages/admin/ApporteursPage'));
const CollaborateursAdminPage = lazy(() => import('@/pages/admin/CollaborateursPage'));
const SupportAdminPage = lazy(() => import('@/pages/admin/SupportAdminPage'));
const LogsPage = lazy(() => import('@/pages/admin/LogsPage'));
const DocumentsAdminPage = lazy(() => import('@/pages/admin/DocumentsAdminPage'));
const QAConsolePage = lazy(() => import('@/pages/admin/QAConsolePage'));
const SocietesPage = lazy(() => import('@/pages/admin/SociétésPage'));
const CRMPage = lazy(() => import('@/pages/admin/CRMPage'));
const ClassementPage = lazy(() => import('@/pages/admin/ClassementPage'));
const LogoutPage = lazy(() => import('@/pages/auth/LogoutPage'));

import RemunerationPage from '@/pages/admin/RemunerationPage';

const routes: RouteObject[] = [
  {
    element: <PublicLayout />,
    children: [
      { index: true, element: <HomePage /> },
      
      // Vision
      { path: 'vision', element: <VisionPage /> },
      
      // Comment ça marche
      { path: 'comment-ca-marche', element: <CommentCaMarchePage /> },
      
      // FAQ
      { path: 'faq', element: <FAQPage /> },

      // Apporteurs d'affaires routes
      { path: 'apporteurs-affaires', element: <ApporteursAffairesPage /> },
      { path: 'apporteurs-affaires/ville/:citySlug', element: <ApporteursAffairesCityPage /> },
      { path: 'apporteurs-affaires/ville/:citySlug/secteur/:sectorSlug', element: <AOCitySectorPage /> },
      { path: 'apporteurs-affaires/secteurs', element: <AOSectorsIndexPage /> },
      { path: 'apporteurs-affaires/secteur/:slug', element: <AOSectorPage /> },
      
      // Collaborateurs routes
      { path: 'collaborateurs', element: <CollaborateursPage /> },
      { path: 'collaborateurs/ville/:citySlug', element: <CollaborateursCityPage /> },
      { path: 'collaborateurs/ville/:citySlug/secteur/:sectorSlug', element: <CollabCitySectorPage /> },
      { path: 'collaborateurs/secteurs', element: <CollabSectorsIndexPage /> },
      { path: 'collaborateurs/secteur/:slug', element: <CollabSectorPage /> },
      
      // Villes, départements, régions
      { path: 'villes', element: <VillesPage /> },
      { path: 'departements', element: <DepartementsIndexPage /> },
      { path: 'departements/:slug', element: <DepartementCityListPage /> },
      { path: 'regions', element: <RegionsIndexPage /> },
      { path: 'regions/:slug', element: <RegionCityListPage /> },
      
      // Blog
      { path: 'blog', element: <BlogListPage /> },
      { path: 'blog/:slug', element: <BlogPostPage /> },
      
      // Auth routes
      { path: 'login', element: <><NoIndex /><LoginPage /></> },
      { path: 'signup', element: <><NoIndex /><SignupPage /></> },
      { path: 'inscription-confirmee', element: <><NoIndex /><InscriptionConfirmeePage /></> },
      { path: 'reset', element: <><NoIndex /><ResetPage /></> },
      
      // Redirects de compatibilité auth
      { path: 'reset-password', element: <Navigate to="/reset" replace /> },
      
      // Legal
      { path: 'mentions-legales', element: <MentionsLegalesPage /> },
      { path: 'cgu', element: <CGUPage /> },
      { path: 'politique-confidentialite', element: <PolitiqueConfidentialitePage /> },
      { path: 'cookies', element: <CookiesPage /> },
      
      // Merci routes
      { path: 'merci/ao', element: <><NoIndex /><MerciAOPage /></> },
      { path: 'merci/commercant', element: <><NoIndex /><MerciCommercePage /></> },
      
      // Redirects de compatibilité merci
      { path: 'merci-ao', element: <Navigate to="/merci/ao" replace /> },
      { path: 'merci-commerce', element: <Navigate to="/merci/commercant" replace /> },
      
      // Logout route
      { path: 'logout', element: <LogoutPage /> },
    ],
  },
  // ── Dashboard ──
  {
    path: '/dashboard',
    element: (
      <ProtectedRoute>
        <DashboardLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <DashboardRouterPage /> },
      { path: 'compte', element: <ComptePage /> },
      { path: 'candidature', element: <CandidaturePage /> },
      { path: 'support', element: <SupportPage /> },
      { path: 'notifications', element: <NotificationsPage /> },
      // AO routes — protégées par rôle AO
      {
        path: 'ao',
        element: <ProtectedRoute allowedRoles={['AO']} />,
        children: [
          { index: true, element: <Navigate to="overview" replace /> },
          { path: 'overview', element: <AOOverviewPage /> },
          { path: 'opportunites', element: <AOOpportunitesPage /> },
          { path: 'commissions', element: <AOCommissionsPage /> },
          { path: 'classement', element: <AOClassementPage /> },
          { path: 'affilies', element: <AOAffiliesPage /> },
          { path: 'parrainage', element: <AOParrainagePage /> },
        ],
      },
      // Collaborateur routes — protégées par rôle COMMERCE
      {
        path: 'collaborateur',
        element: <ProtectedRoute allowedRoles={['COMMERCE']} />,
        children: [
          { index: true, element: <Navigate to="overview" replace /> },
          { path: 'overview', element: <CollabOverviewPage /> },
          { path: 'documents', element: <CollabDocumentsPage /> },
          { path: 'marques', element: <CollabMarquesPage /> },
          { path: 'rendez-vous', element: <CollabRendezVousPage /> },
        ],
      },
    ],
  },
  // ── Admin ──
  {
    path: '/admin',
    element: (
      <ProtectedRoute requireAdmin={true}>
        <AdminLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <AdminPage /> },
      { path: 'qa', element: <QAConsolePage /> },
      { path: 'utilisateurs', element: <UtilisateursPage /> },
      { path: 'apporteurs', element: <ApporteursPage /> },
      { path: 'collaborateurs', element: <CollaborateursAdminPage /> },
      { path: 'candidatures', element: <CandidaturesPage /> },
      { path: 'societes', element: <SocietesPage /> },
      { path: 'crm', element: <CRMPage /> },
      { path: 'classement', element: <ClassementPage /> },
      { path: 'statistiques', element: <StatistiquesPage /> },
      { path: 'logs', element: <LogsPage /> },
      { path: 'support', element: <SupportAdminPage /> },
      { path: 'documents', element: <DocumentsAdminPage /> },
      { path: 'remuneration', element: <RemunerationPage /> },
      // Alias de compatibilité
      { path: 'crm-remuneration', element: <Navigate to="/admin/remuneration" replace /> },
    ],
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
];

export default routes;